/**
 * Reads a PDF into positioned text so the dispatch parsers can work by layout
 * ("the value to the right of `Load Number:`", "the cell under `Terminal
 * Name`") instead of by reading order, which text extraction scrambles for
 * these forms.
 */

export interface LayoutItem {
  text: string;
  x: number;
  y: number;
  width: number;
}

export interface LayoutLine {
  y: number;
  items: LayoutItem[];
  text: string;
}

export interface Layout {
  items: LayoutItem[];
  lines: LayoutLine[];
  text: string;
}

/** The parts of a pdf.js text item the layout needs. */
export interface RawTextItem {
  str: string;
  transform: number[];
  width?: number;
}

// Items within this many points vertically sit on the same line.
export const LINE_TOLERANCE = 3;

/**
 * Joins items left to right, gluing fragments that touch (e.g. "(951) 352",
 * "-", "2631") and spacing ones that don't.
 */
export const joinItems = (items: LayoutItem[]): string => {
  let out = '';
  let end: number | null = null;
  for (const item of items) {
    const gap = end === null ? 0 : item.x - end;
    out += out && gap > 2 ? ` ${item.text}` : item.text;
    end = item.x + item.width;
  }
  return out.replace(/\s+/g, ' ').trim();
};

/** Groups items into top-to-bottom lines, each ordered left to right. */
const groupLines = (items: LayoutItem[]): LayoutLine[] => {
  const sorted = [...items].sort((a, b) => b.y - a.y || a.x - b.x);
  const lines: Array<{ y: number; items: LayoutItem[] }> = [];
  for (const item of sorted) {
    const line = lines.find(l => Math.abs(l.y - item.y) <= LINE_TOLERANCE);
    if (line) line.items.push(item);
    else lines.push({ y: item.y, items: [item] });
  }
  return lines.map(line => {
    line.items.sort((a, b) => a.x - b.x);
    return { ...line, text: joinItems(line.items) };
  });
};

/** Builds the layout from one page's pdf.js text items. */
export const buildLayout = (rawItems: RawTextItem[]): Layout => {
  const items = rawItems
    .map(item => ({
      // Some forms pad with control characters; drop them.
      // eslint-disable-next-line no-control-regex
      text: (item.str || '').replace(/[\u0000-\u001f]/g, ' ').replace(/\s+/g, ' ').trim(),
      x: item.transform[4],
      y: item.transform[5],
      width: item.width || 0
    }))
    .filter(item => item.text);
  const lines = groupLines(items);
  return { items, lines, text: lines.map(line => line.text).join('\n') };
};

export const findItem = (items: LayoutItem[], pattern: RegExp): LayoutItem | undefined =>
  items.find(item => pattern.test(item.text));
